import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, User, Phone, Clock } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import StatusBadge from '../../components/StatusBadge';
import * as vendorApi from '../../api/vendor.api';
import { errorMessage } from '../../api/client';

// Client 2026-08-04: clinic calendar — Day / Week / Month views.
// Day view = time-gutter with per-slot booking cards.
// Week view = 7 columns × hour grid with booking cards spanning slots.
// Month view = classic 6×7 month grid with per-day badges + tooltip.

const HOUR_HEIGHT = 60; // px per hour (day + week)
const DAY_NAMES_LONG  = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_NAMES_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// ─── date helpers ──────────────────────────────────────────────────────
function pad(n) { return String(n).padStart(2, '0'); }
function ymdOf(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function todayYmd() { return ymdOf(new Date()); }
function fromYmd(ymd) {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d);
}
function shiftYmd(ymd, deltaDays) {
  const d = fromYmd(ymd);
  d.setDate(d.getDate() + deltaDays);
  return ymdOf(d);
}
function hhmmToMin(hhmm) {
  const [h, m] = String(hhmm || '').split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
}

// Monday-of-week for a given date (ISO-style: week starts Monday).
function startOfWeek(ymd) {
  const d = fromYmd(ymd);
  const wd = d.getDay(); // 0=Sun
  const monOffset = (wd + 6) % 7; // Sun→6, Mon→0, Tue→1, …
  d.setDate(d.getDate() - monOffset);
  return ymdOf(d);
}

// First cell of the month grid (Monday on/before the 1st).
function startOfMonthGrid(ymd) {
  const [y, m] = ymd.split('-').map(Number);
  const first = new Date(y, m - 1, 1);
  return startOfWeek(ymdOf(first));
}

function humanDate(ymd) {
  return fromYmd(ymd).toLocaleDateString(undefined, {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
}
function humanMonth(ymd) {
  return fromYmd(ymd).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

// ─── view toggle button ────────────────────────────────────────────────
function ViewToggle({ view, onChange }) {
  const btn = (v, label) => (
    <button
      type="button"
      onClick={() => onChange(v)}
      className={view === v ? 'btn btn-primary' : 'btn btn-outline'}
      style={{ padding: '6px 12px', fontSize: 13 }}
    >
      {label}
    </button>
  );
  return (
    <div style={{ display: 'inline-flex', gap: 6 }}>
      {btn('day',   'Day')}
      {btn('week',  'Week')}
      {btn('month', 'Month')}
    </div>
  );
}

// ─── main page ─────────────────────────────────────────────────────────
function ClinicCalendar() {
  const [view, setView] = useState('day');            // 'day' | 'week' | 'month'
  const [anchor, setAnchor] = useState(todayYmd());   // day for day view, any day in week/month for others
  const [loading, setLoading] = useState(true);
  const [dayData, setDayData]   = useState(null);
  const [rangeData, setRangeData] = useState(null);

  // Compute the range endpoints based on the current view + anchor.
  const { startYmd, endYmd, days } = useMemo(() => {
    if (view === 'week') {
      const start = startOfWeek(anchor);
      const arr = Array.from({ length: 7 }, (_, i) => shiftYmd(start, i));
      return { startYmd: arr[0], endYmd: arr[6], days: arr };
    }
    if (view === 'month') {
      const start = startOfMonthGrid(anchor);
      const arr = Array.from({ length: 42 }, (_, i) => shiftYmd(start, i)); // 6×7
      return { startYmd: arr[0], endYmd: arr[41], days: arr };
    }
    return { startYmd: anchor, endYmd: anchor, days: [anchor] };
  }, [view, anchor]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        if (view === 'day') {
          const res = await vendorApi.getDaySchedule(anchor);
          if (!cancelled) setDayData(res);
        } else {
          const res = await vendorApi.getRangeSchedule(startYmd, endYmd);
          if (!cancelled) setRangeData(res);
        }
      } catch (e) {
        if (!cancelled) toast.error(errorMessage(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [view, anchor, startYmd, endYmd]);

  // Nav step size depends on the view.
  function shift(delta) {
    if (view === 'day')   return setAnchor(shiftYmd(anchor, delta));
    if (view === 'week')  return setAnchor(shiftYmd(anchor, delta * 7));
    // month: move by one calendar month, snap to the 1st for consistency
    const d = fromYmd(anchor);
    d.setMonth(d.getMonth() + delta, 1);
    setAnchor(ymdOf(d));
  }

  // Title strip shows what the current view is anchored on.
  const title =
      view === 'day'   ? humanDate(anchor)
    : view === 'week'  ? `Week of ${fromYmd(startYmd).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} – ${fromYmd(endYmd).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}`
    : humanMonth(anchor);

  return (
    <>
      <PageHeader
        title="Clinic calendar"
        subtitle="Bookings assigned to your clinic. Switch between day, week, or month."
        actions={
          <Link to="/clinic/booking-settings" className="btn btn-outline">
            Booking settings
          </Link>
        }
      />

      {/* Nav + view toggle strip */}
      <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 'var(--s-4)', flexWrap: 'wrap' }}>
        <button className="btn btn-ghost" onClick={() => shift(-1)} aria-label="Previous">
          <ChevronLeft size={16} />
        </button>
        <div style={{ flex: 1, textAlign: 'center', minWidth: 220 }}>
          <div className="bold" style={{ fontSize: 16 }}>{title}</div>
        </div>
        <button className="btn btn-ghost" onClick={() => shift(+1)} aria-label="Next">
          <ChevronRight size={16} />
        </button>
        <button className="btn btn-ghost" onClick={() => setAnchor(todayYmd())}>
          <CalendarIcon size={14} /> Today
        </button>
        <ViewToggle view={view} onChange={setView} />
      </div>

      {loading ? (
        <LoadingSpinner size="lg" />
      ) : view === 'day'   ? <DayView data={dayData} />
      : view === 'week'    ? <WeekView data={rangeData} days={days} onDayClick={(ymd) => { setAnchor(ymd); setView('day'); }} />
      : /* month */          <MonthView data={rangeData} days={days} anchor={anchor} onDayClick={(ymd) => { setAnchor(ymd); setView('day'); }} />}
    </>
  );
}

// ─── DAY view ──────────────────────────────────────────────────────────
function DayView({ data }) {
  if (!data) return null;
  const { firstHour, lastHour, slotLen } = computeGridRange(
    data.settings,
    data.bookings || [],
  );
  const hours     = Array.from({ length: Math.max(1, lastHour - firstHour) }, (_, i) => firstHour + i);
  const gridStartMin = firstHour * 60;
  const minToTop = (m) => ((m - gridStartMin) / 60) * HOUR_HEIGHT;
  void slotLen;
  const bookingCount = (data.bookings || []).filter((o) => !!o?.scheduledAt).length;

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      {!data.isWorkingDay && (
        <div className="notice warning" style={{ margin: 12, padding: '8px 12px', fontSize: 13 }}>
          Non-working day (closed for bookings)
        </div>
      )}
      {bookingCount === 0 && (
        <div className="muted" style={{ padding: '12px 14px', fontSize: 13 }}>
          No appointments booked for this day.
        </div>
      )}
      <div
        style={{
          position: 'relative',
          display: 'grid',
          gridTemplateColumns: '72px 1fr',
          height: hours.length * HOUR_HEIGHT,
          background: 'var(--surface, #fff)',
        }}
      >
        {hours.map((h, i) => (
          <React.Fragment key={h}>
            <div style={hourGutterCellStyle(i === 0)}>{pad(h)}:00</div>
            <div style={{ borderTop: i === 0 ? 'none' : '1px solid var(--border, #e5e7eb)', position: 'relative' }} />
          </React.Fragment>
        ))}
        <BreakOverlays breaks={data.settings?.breaks || []} minToTop={minToTop} left={72} />
        {(data.bookings || []).map((o) => (
          <BookingCard
            key={o._id}
            order={o}
            top={minToTop(hhOfDate(o.scheduledAt) * 60 + mmOfDate(o.scheduledAt))}
            height={((data.settings?.slotDurationMin || 30) / 60) * HOUR_HEIGHT}
            left={76}
            right={8}
          />
        ))}
      </div>
    </div>
  );
}

// ─── WEEK view ─────────────────────────────────────────────────────────
function WeekView({ data, days, onDayClick }) {
  if (!data) return null;
  const { firstHour, lastHour, slotLen } = computeGridRange(
    data.settings,
    data.bookings || [],
  );
  const hours     = Array.from({ length: Math.max(1, lastHour - firstHour) }, (_, i) => firstHour + i);
  const gridStartMin = firstHour * 60;
  const minToTop = (m) => ((m - gridStartMin) / 60) * HOUR_HEIGHT;
  const workingDays = data.settings?.workingDays || [1,2,3,4,5];
  const holidays    = new Set(data.settings?.holidays || []);
  const bookingsByYmd = groupBookingsByYmd(data.bookings || []);
  const todayY = todayYmd();

  return (
    <div className="card" style={{ padding: 0, overflow: 'auto' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `72px repeat(7, minmax(120px, 1fr))`,
          borderBottom: '1px solid var(--border, #e5e7eb)',
          position: 'sticky', top: 0, background: 'var(--surface, #fff)', zIndex: 2,
        }}
      >
        <div />
        {days.map((ymd) => {
          const d = fromYmd(ymd);
          const closed = !workingDays.includes(d.getDay()) || holidays.has(ymd);
          const isToday = ymd === todayY;
          return (
            <button
              key={ymd}
              type="button"
              onClick={() => onDayClick(ymd)}
              style={{
                border: 0, background: 'none', cursor: 'pointer',
                padding: '10px 8px', textAlign: 'center',
                borderLeft: '1px solid var(--border, #e5e7eb)',
                color: closed ? 'var(--ink-500,#6b7280)' : 'var(--ink-900,#111827)',
                background: isToday ? 'var(--brand-50,#ecfeff)' : 'transparent',
              }}
              title="Open day view"
            >
              <div className="text-xs muted">{DAY_NAMES_SHORT[d.getDay()]}</div>
              <div className={isToday ? 'bold' : ''} style={{ fontSize: 18, marginTop: 2 }}>
                {d.getDate()}
              </div>
              {closed && <div className="text-xs" style={{ color: 'var(--amber-600,#d97706)' }}>Closed</div>}
            </button>
          );
        })}
      </div>

      <div
        style={{
          position: 'relative',
          display: 'grid',
          gridTemplateColumns: `72px repeat(7, minmax(120px, 1fr))`,
          height: hours.length * HOUR_HEIGHT,
        }}
      >
        {/* Hour rows */}
        {hours.map((h, i) => (
          <React.Fragment key={`h-${h}`}>
            <div style={hourGutterCellStyle(i === 0)}>{pad(h)}:00</div>
            {days.map((ymd) => (
              <div
                key={`${h}-${ymd}`}
                style={{
                  borderTop:  i === 0 ? 'none' : '1px solid var(--border, #e5e7eb)',
                  borderLeft: '1px solid var(--border, #e5e7eb)',
                }}
              />
            ))}
          </React.Fragment>
        ))}

        {/* Break overlays per column */}
        {days.map((ymd, colIdx) => (
          <BreakOverlays
            key={`brk-${ymd}`}
            breaks={data.settings?.breaks || []}
            minToTop={minToTop}
            left={72 + colIdx * ((100 / 7) * 0.01) /* unused */}
            column={{ index: colIdx, count: 7 }}
          />
        ))}

        {/* Booking cards, per day column */}
        {days.map((ymd, colIdx) => {
          const dayBookings = bookingsByYmd.get(ymd) || [];
          return dayBookings.map((o) => {
            const mins = hhOfDate(o.scheduledAt) * 60 + mmOfDate(o.scheduledAt);
            const top = minToTop(mins);
            const height = (slotLen / 60) * HOUR_HEIGHT;
            const leftPct  = (72 / 8) + 0; // unused, we use CSS calc below via style
            return (
              <BookingCardCol
                key={o._id}
                order={o}
                top={top}
                height={height}
                colIndex={colIdx}
                colCount={7}
              />
            );
          });
        })}
      </div>
    </div>
  );
}

// ─── MONTH view ────────────────────────────────────────────────────────
function MonthView({ data, days, anchor, onDayClick }) {
  if (!data) return null;
  const workingDays = data.settings?.workingDays || [1,2,3,4,5];
  const holidays    = new Set(data.settings?.holidays || []);
  const bookingsByYmd = groupBookingsByYmd(data.bookings || []);
  const currentMonth = fromYmd(anchor).getMonth();
  const todayY = todayYmd();

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      {/* Weekday header row */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          borderBottom: '1px solid var(--border, #e5e7eb)',
          background: 'var(--surface-2, #f9fafb)',
        }}
      >
        {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map((n) => (
          <div key={n} style={{ padding: '8px 10px', fontSize: 12, color: 'var(--ink-500,#6b7280)', fontWeight: 600 }}>
            {n}
          </div>
        ))}
      </div>

      {/* 6 rows × 7 cols */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gridAutoRows: 'minmax(96px, 1fr)' }}>
        {days.map((ymd) => {
          const d = fromYmd(ymd);
          const wd = d.getDay();
          const dayBookings = bookingsByYmd.get(ymd) || [];
          const outOfMonth = d.getMonth() !== currentMonth;
          const closed = !workingDays.includes(wd) || holidays.has(ymd);
          const isToday = ymd === todayY;
          return (
            <button
              key={ymd}
              type="button"
              onClick={() => onDayClick(ymd)}
              style={{
                border: 0, textAlign: 'left', cursor: 'pointer',
                padding: 8,
                borderTop:  '1px solid var(--border, #e5e7eb)',
                borderLeft: '1px solid var(--border, #e5e7eb)',
                background: outOfMonth ? 'var(--surface-2,#f9fafb)' : 'var(--surface,#fff)',
                color:      outOfMonth ? 'var(--ink-400,#9ca3af)' : 'var(--ink-900,#111827)',
                display: 'flex', flexDirection: 'column', gap: 4,
                minHeight: 96,
                position: 'relative',
              }}
              title={dayBookings.length ? `${dayBookings.length} booking(s)` : 'Open day view'}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span className={isToday ? 'bold' : ''} style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  minWidth: 22, height: 22, borderRadius: 999,
                  background: isToday ? 'var(--brand-500,#14b8a6)' : 'transparent',
                  color: isToday ? '#fff' : 'inherit',
                  fontSize: 13, padding: '0 6px',
                }}>{d.getDate()}</span>
                {closed && !outOfMonth && (
                  <span className="text-xs" style={{ color: 'var(--amber-600,#d97706)' }}>closed</span>
                )}
              </div>
              {dayBookings.slice(0, 3).map((o) => (
                <div
                  key={o._id}
                  style={{
                    fontSize: 11,
                    padding: '2px 6px',
                    borderRadius: 4,
                    background: 'var(--brand-50,#ecfeff)',
                    borderLeft: '2px solid var(--brand-500,#14b8a6)',
                    color: 'var(--ink-900,#111827)',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}
                >
                  {new Date(o.scheduledAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                  {' '}
                  {o.user?.name || o.bookingId || o.orderNumber}
                </div>
              ))}
              {dayBookings.length > 3 && (
                <div className="text-xs muted">+{dayBookings.length - 3} more</div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── shared bits ───────────────────────────────────────────────────────
function hourGutterCellStyle(isFirst) {
  return {
    borderTop: isFirst ? 'none' : '1px solid var(--border, #e5e7eb)',
    padding: '4px 8px',
    fontSize: 12,
    color: 'var(--ink-500, #6b7280)',
    fontVariantNumeric: 'tabular-nums',
  };
}

function hhOfDate(iso) { return new Date(iso).getHours(); }
function mmOfDate(iso) { return new Date(iso).getMinutes(); }

// Client 2026-08-06: bug fix — earlier the grid used ONLY the clinic's
// declared open hours, so any booking scheduled outside that window
// (e.g. a legacy 08:00 slot when settings say start at 09:00) fell
// off-screen and looked "missing". Union the declared range with the
// actual bookings so every booking is visible in day + week views.
function computeGridRange(settings, bookings) {
  const openStart = hhmmToMin(settings?.startTime || '09:00') ?? 540;
  const openEnd   = hhmmToMin(settings?.endTime   || '18:00') ?? 1080;
  const slotLen   = Number(settings?.slotDurationMin) || 30;

  let earliestMin = openStart;
  let latestMin   = openEnd;
  for (const o of bookings || []) {
    if (!o?.scheduledAt) continue;
    const d = new Date(o.scheduledAt);
    if (Number.isNaN(d.getTime())) continue;
    const mins = d.getHours() * 60 + d.getMinutes();
    if (mins < earliestMin) earliestMin = mins;
    if (mins + slotLen > latestMin) latestMin = mins + slotLen;
  }

  return {
    firstHour: Math.floor(earliestMin / 60),
    lastHour:  Math.ceil(latestMin / 60),
    slotLen,
  };
}

function groupBookingsByYmd(bookings) {
  const map = new Map();
  for (const o of bookings) {
    const d = new Date(o.scheduledAt);
    const ymd = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const arr = map.get(ymd) || [];
    arr.push(o);
    map.set(ymd, arr);
  }
  return map;
}

// Break overlay for a single column area (Day view: full width; Week
// view: aligned to a specific 1/7 column).
function BreakOverlays({ breaks, minToTop, column }) {
  return breaks.map((b, i) => {
    const bs = hhmmToMin(b.start);
    const be = hhmmToMin(b.end);
    if (bs === null || be === null) return null;
    const top    = minToTop(bs);
    const height = ((be - bs) / 60) * HOUR_HEIGHT;
    // Position: full-width in day view, one column in week view.
    const style = column
      ? {
          position: 'absolute',
          top, height,
          left:  `calc(72px + ${column.index} * ((100% - 72px) / ${column.count}))`,
          width: `calc((100% - 72px) / ${column.count})`,
        }
      : { position: 'absolute', top, height, left: 72, right: 0 };
    return (
      <div
        key={`brk-${i}-${column?.index ?? 'd'}`}
        style={{
          ...style,
          background: 'repeating-linear-gradient(45deg, rgba(245,158,11,.08), rgba(245,158,11,.08) 8px, transparent 8px, transparent 16px)',
          borderTop:    '1px dashed rgba(245,158,11,.5)',
          borderBottom: '1px dashed rgba(245,158,11,.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 11, color: 'var(--amber-700, #b45309)', pointerEvents: 'none',
        }}
      >
        <Clock size={12} style={{ marginRight: 4 }} />
        {b.label || 'Break'} · {b.start}–{b.end}
      </div>
    );
  });
}

// Day-view booking card (spans full column width, minus gutter).
function BookingCard({ order, top, height, left, right }) {
  const start = new Date(order.scheduledAt);
  const patientName  = order.user?.name  || 'Patient';
  const patientPhone = order.user?.phone || '';
  return (
    <Link
      to={`/orders/${order._id}`}
      style={{
        position: 'absolute',
        left, right,
        top: top + 1, height: Math.max(28, height - 2),
        background: 'var(--brand-50, #ecfeff)',
        borderLeft: '3px solid var(--brand-500, #14b8a6)',
        borderRadius: 6,
        padding: '6px 8px',
        textDecoration: 'none',
        color: 'var(--ink-900, #111827)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        boxShadow: '0 1px 2px rgba(0,0,0,.06)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
        <User size={12} />
        <strong style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{patientName}</strong>
        <StatusBadge status={order.status} size="xs" />
      </div>
      <div className="text-xs muted" style={{ marginTop: 2, display: 'flex', gap: 8 }}>
        <span>{start.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}</span>
        {patientPhone && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><Phone size={11} /> {patientPhone}</span>}
        <span style={{ marginLeft: 'auto' }}>{order.bookingId || order.orderNumber}</span>
      </div>
    </Link>
  );
}

// Week-view booking card — positioned to a specific 1/N column.
function BookingCardCol({ order, top, height, colIndex, colCount }) {
  const start = new Date(order.scheduledAt);
  const patientName = order.user?.name || 'Patient';
  return (
    <Link
      to={`/orders/${order._id}`}
      style={{
        position: 'absolute',
        top: top + 1, height: Math.max(24, height - 2),
        left:  `calc(72px + ${colIndex} * ((100% - 72px) / ${colCount}) + 3px)`,
        width: `calc((100% - 72px) / ${colCount} - 6px)`,
        background: 'var(--brand-50, #ecfeff)',
        borderLeft: '3px solid var(--brand-500, #14b8a6)',
        borderRadius: 6,
        padding: '4px 6px',
        textDecoration: 'none',
        color: 'var(--ink-900, #111827)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        boxShadow: '0 1px 2px rgba(0,0,0,.06)',
      }}
      title={`${patientName} · ${order.bookingId || order.orderNumber}`}
    >
      <div style={{ fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {start.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })} {patientName}
      </div>
      <div className="text-xs muted" style={{ fontSize: 10, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {order.bookingId || order.orderNumber}
      </div>
    </Link>
  );
}

// Silence unused-import warnings by referencing the long name array (was
// used pre-refactor; kept because callers might drop back to it).
void DAY_NAMES_LONG;

export default ClinicCalendar;
